import { Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { ImageInput } from './MediaInputs';
import { isValidSlug } from './adminValidation';
import type { EditFormProps } from '../../types/admin';

export function EditForm({ item, type, onSave, onCancel, onChange }: EditFormProps) {
  const handleChange = (field: string, value: any) => {
    onChange(field, value);
  };

  const commonFields = (
    <>
      {type !== 'team' && type !== 'pages' && (
        <div>
          <Label className="text-white">Slug (URL)</Label>
          <Input
            value={item.slug || ''}
            onChange={(e) => handleChange('slug', e.target.value)}
            className="bg-zinc-900/50 border-white/10 text-white"
            placeholder="my-item-url"
          />
          {item.slug && !isValidSlug(item.slug) && (
            <p className="text-red-300 text-xs mt-1">Use lowercase kebab-case only.</p>
          )}
        </div>
      )}
      <div>
        <Label className="text-white">Publication status</Label>
        <select
          value={item.publication_status || 'draft'}
          onChange={(e) => handleChange('publication_status', e.target.value)}
          className="mt-2 w-full bg-zinc-900/50 border border-white/10 text-white rounded-lg px-4 py-2"
        >
          <option value="draft">Draft</option>
          <option value="published">Published</option>
        </select>
      </div>
      {type !== 'pages' && (
        <>
          <div>
            <Label className="text-white">{type === 'team' ? 'Имя' : 'Название'}</Label>
            <Input
              value={item.name || item.title || ''}
              onChange={(e) => handleChange(type === 'cases' || type === 'events' || type === 'articles' ? 'title' : 'name', e.target.value)}
              className="bg-zinc-900/50 border-white/10 text-white"
            />
          </div>
          <div>
            <Label className="text-white">Описание</Label>
            <Textarea
              value={item.description || item.excerpt || ''}
              onChange={(e) => handleChange(type === 'articles' ? 'excerpt' : 'description', e.target.value)}
              className="bg-zinc-900/50 border-white/10 text-white"
              rows={3}
            />
          </div>
          <div>
            <Label className="text-white">URL изображения</Label>
            <ImageInput
              value={item.image_url || item.logo_url || ''}
              onChange={(val) => handleChange(type === 'partners' ? 'logo_url' : 'image_url', val)}
              placeholder="https://example.com/image.jpg"
            />
          </div>
        </>
      )}
    </>
  );

  const typeSpecificFields = {
    services: (
      <div>
        <Label className="text-white">Особенности (через запятую)</Label>
        <Input
          value={(item.features || []).join(', ')}
          onChange={(e) => handleChange('features', e.target.value.split(',').map((s) => s.trim()))}
          className="bg-zinc-900/50 border-white/10 text-white"
        />
      </div>
    ),
    cases: (
      <>
        <div>
          <Label className="text-white">Клиент</Label>
          <Input value={item.client || ''} onChange={(e) => handleChange('client', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" />
        </div>
        <div>
          <Label className="text-white">Категория</Label>
          <Input value={item.category || ''} onChange={(e) => handleChange('category', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" />
        </div>
        <div>
          <Label className="text-white">Задача</Label>
          <Textarea value={item.challenge || ''} onChange={(e) => handleChange('challenge', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" rows={2} />
        </div>
        <div>
          <Label className="text-white">Решение</Label>
          <Textarea value={item.solution || ''} onChange={(e) => handleChange('solution', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" rows={2} />
        </div>
        <div>
          <Label className="text-white">Результаты (через запятую)</Label>
          <Input value={(item.results || []).join(', ')} onChange={(e) => handleChange('results', e.target.value.split(',').map((s) => s.trim()))} className="bg-zinc-900/50 border-white/10 text-white" />
        </div>
      </>
    ),
    events: (
      <>
        <div>
          <Label className="text-white">Дата</Label>
          <Input type="date" value={item.date || ''} onChange={(e) => handleChange('date', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" />
        </div>
        <div>
          <Label className="text-white">Место</Label>
          <Input value={item.location || ''} onChange={(e) => handleChange('location', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" />
        </div>
        <div>
          <Label className="text-white">Тип</Label>
          <Input value={item.type || ''} onChange={(e) => handleChange('type', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" />
        </div>
      </>
    ),
    projects: (
      <>
        <div>
          <Label className="text-white">Стадия</Label>
          <Input value={item.stage || ''} onChange={(e) => handleChange('stage', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" />
        </div>
        <div>
          <Label className="text-white">Индустрия</Label>
          <Input value={item.industry || ''} onChange={(e) => handleChange('industry', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" />
        </div>
        <div>
          <Label className="text-white">Страна</Label>
          <Input value={item.country || ''} onChange={(e) => handleChange('country', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" />
        </div>
        <div>
          <Label className="text-white">Требуемый капитал</Label>
          <Input value={item.capital_required || ''} onChange={(e) => handleChange('capital_required', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" />
        </div>
        <div>
          <Label className="text-white">Сроки</Label>
          <Input value={item.timeline || ''} onChange={(e) => handleChange('timeline', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" />
        </div>
      </>
    ),
    partners: (
      <>
        <div>
          <Label className="text-white">Категории (через запятую)</Label>
          <Input value={(item.categories || []).join(', ')} onChange={(e) => handleChange('categories', e.target.value.split(',').map((s) => s.trim()))} className="bg-zinc-900/50 border-white/10 text-white" />
        </div>
        <div>
          <Label className="text-white">Страна</Label>
          <Input value={item.country || ''} onChange={(e) => handleChange('country', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" />
        </div>
      </>
    ),
    articles: (
      <>
        <div>
          <Label className="text-white">Контент</Label>
          <Textarea value={item.content || ''} onChange={(e) => handleChange('content', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" rows={8} />
        </div>
        <div>
          <Label className="text-white">Автор</Label>
          <Input value={item.author || ''} onChange={(e) => handleChange('author', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" />
        </div>
        <div>
          <Label className="text-white">Категория</Label>
          <Input value={item.category || ''} onChange={(e) => handleChange('category', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" />
        </div>
      </>
    ),
    team: (
      <>
        <div>
          <Label className="text-white">Должность</Label>
          <Input value={item.position || ''} onChange={(e) => handleChange('position', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" />
        </div>
        <div>
          <Label className="text-white">Биография</Label>
          <Textarea value={item.bio || ''} onChange={(e) => handleChange('bio', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" rows={3} />
        </div>
        <div>
          <Label className="text-white">LinkedIn</Label>
          <Input value={item.linkedin || ''} onChange={(e) => handleChange('linkedin', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" />
        </div>
      </>
    ),
    pages: (
      <>
        <div>
          <Label className="text-white">Slug (URL: privacy, terms, nda, download)</Label>
          <Input value={item.slug || ''} onChange={(e) => handleChange('slug', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" placeholder="privacy" />
        </div>
        <div>
          <Label className="text-white">Заголовок (RU)</Label>
          <Input value={item.title || ''} onChange={(e) => handleChange('title', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white" />
        </div>
        <div>
          <Label className="text-white">Контент (RU) - поддерживает HTML</Label>
          <Textarea value={item.content || ''} onChange={(e) => handleChange('content', e.target.value)} className="bg-zinc-900/50 border-white/10 text-white font-mono text-sm" rows={15} />
        </div>
        <p className="text-white/40 text-sm">
          * Английская версия будет создана автоматически при сохранении
        </p>
      </>
    ),
  };

  return (
    <div className="glass rounded-2xl p-6 mb-6 space-y-4">
      {commonFields}
      {typeSpecificFields[type]}
      <div className="flex gap-4 pt-4">
        <Button onClick={onCancel} className="flex-1 bg-white/10 hover:bg-white/20 text-white rounded-xl">
          Отмена
        </Button>
        <Button onClick={onSave} className="flex-1 bg-[#E11D2E] hover:bg-[#E11D2E]/90 text-white rounded-xl" data-testid="save-button">
          <Save className="w-5 h-5 mr-2" />
          Сохранить
        </Button>
      </div>
    </div>
  );
}
